import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EntryStoreState } from './entry.model';
import { EntryActions } from './entry.actions';
import {
  catchError,
  EMPTY,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { CommonService } from '../common.service';
import { EntrySelectors } from './entry.selectors';
import { ConfigService } from '../config.service';
import { EntryData } from '../entry/structure-data.models';
import { fromFetch } from 'rxjs/fetch';
import { EntryService } from '../entry/entry.service';
import { UtilService } from '../util.service';
import { Router } from '@angular/router';
@Injectable()
export class EntryEffects {
  private readonly entryAPIService = inject(CommonService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store<EntryStoreState>);
  private configService = inject(ConfigService);
  private entryService = inject(EntryService);
  private utilService = inject(UtilService)
  private readonly router = inject(Router);
  
  getEntryData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getEntryData),
      switchMap(() => this.store.select(EntrySelectors.accession).pipe(tap())),
      mergeMap((accession: string) => {
        const auth = this.configService.getConfig().apiKey
          ? `?key=${this.configService.getConfig().apiKey}`
          : '';
        return this.entryAPIService
          .getApiData(
            this.configService.getConfig().entryApiUrl + accession + auth,
            { isInternal: true }
          )
          .pipe(
            map((entryData) =>{
              if(entryData.length > 1){
                this.router.navigate(
                  ['/search', 'text', accession],
                  // { queryParams: { redirected: true } }
                );
                return;
              }
              return EntryActions.getEntryDataSuccess({ entryData: entryData[0] })
            }),
            catchError((error) => {
              this.entryService.setShowError(true);
              // this.store.dispatch(EntryActions.getEntryDataSuccess({ entryData: undefined }))
              return of(EntryActions.getEntryDataFailure())
            })
          );
      })
    )
  );

  getUniprotData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getUniprotData),
      switchMap(() => this.store.select(EntrySelectors.uniprotAccession).pipe(tap())),
      mergeMap((uniprotAccession: string) => {
        let accesssionForAPI = uniprotAccession;
        return this.entryAPIService
        .getApiData(this.configService.getConfig().uniprotApiUrl + accesssionForAPI).pipe(
            map((data) =>
              EntryActions.getUniprotDataSuccess({ uniprotData: data })
            ),
            catchError(() => of(EntryActions.getUniprotDataFailure()))
          );
      })
    )
  );

  getCSVData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getCSVData),
      switchMap(() =>
        this.store.select(EntrySelectors.entryData).pipe(take(1))
      ),
      switchMap((entryData: EntryData) => {
        
        // if (entryData?.amAnnotationsUrl) {
          const fileUrl = entryData.amAnnotationsUrl;
         
          return from(fetch(fileUrl)).pipe(
            switchMap(response => from(response.text())),
            map(csvData =>
              EntryActions.getCSVDataSuccess({ csvAmData: csvData })
            ),
            catchError(error => {
              console.error('Fetch or text() failed', error);
              return of(EntryActions.getCSVDataSuccess({ csvAmData: "" }));
            })
          );
        // } else {
        //   return of(EntryActions.getCSVDataSuccess({ csvAmData: undefined }));
        // }
      })
    )
  );

  getDomainData$ = createEffect(() =>
  
    this.actions$.pipe(
      ofType(EntryActions.getTedDomainData),
      switchMap(() => this.store.select(EntrySelectors.accession).pipe(tap())),
      mergeMap((accession: string) => {
        const auth = this.configService.getConfig().apiKey
          ? `?key=${this.configService.getConfig().apiKey}`
          : '';
        let accesssionForAPI = accession
        if(accession.includes('AF-')){
         accesssionForAPI = this.utilService.extractUniprotAccession(accession);
        }
        return this.entryAPIService
          .getApiData(
            this.configService.getConfig().domainsApiUrl + accesssionForAPI + auth,
            { isInternal: true }
          )
          .pipe(
            map((data: any) =>
              EntryActions.getTedDomainDataSuccess({
                tedDomainData: data.annotations,
              })
            ),
            catchError(() => of(EntryActions.getTedDomainDataFailure()))
          );
      })
    )
  );

  getClustermmseqData = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getClustermmseqData),
      switchMap(() => this.store.select(EntrySelectors.uniprotAccession).pipe(tap())),
      mergeMap((uniprotAccession: string) => {
        
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : "";
    const configUrl = this.configService.getConfig().clusterApiUrl;

    const updatedurl = `${configUrl}${uniprotAccession}?cluster_flag=AFDB50%2FMMseqs2&records=10&start=0&sort_direction=DESC&sort_column=averagePlddt`
    return this.entryAPIService
          .getApiData(
            updatedurl + auth,
            { isInternal: true }
          )
          .pipe(
            map((clustermmseqData) =>
              EntryActions.getClustermmseqSuccess({ clustermmseqData: clustermmseqData })
            ),
            catchError(() => of(EntryActions.getClustermmseqFailure()))
          );
        })
    )
  );

  getClusterfoldseekData = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getClusterfoldseekData),
      switchMap(() => this.store.select(EntrySelectors.uniprotAccession).pipe(tap())),
      mergeMap((uniprotAccession: string) => {
        
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : "";
    const configUrl = this.configService.getConfig().clusterApiUrl;

    const updatedurl = `${configUrl}${uniprotAccession}?cluster_flag=AFDB%2FFoldseek&records=10&start=0&sort_direction=DESC&sort_column=averagePlddt`
    return this.entryAPIService
          .getApiData(
            updatedurl + auth,
            { isInternal: true }
          )
          .pipe(
            map((clusterfoldseekData) =>
              EntryActions.getClusterfoldseekSuccess({ clusterfoldseekData: clusterfoldseekData })
            ),
            catchError(() => of(EntryActions.getClusterfoldseekFailure()))
          );
        })
    )
  );
}
